import { useEffect } from "react";
import styled from "styled-components";

const TALLY_EMBED_URL =
      "https://tally.so/embed/RGZMvv?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1";

const C = {
      text: "#3B230A",
      beige: "#F5F0E8",
      beigeMid: "#E8E0D5",
};

function Grain() {
      return (
            <StyledGrainSvg aria-hidden="true">
                  <filter id="suhab-grain-filter">
                        <feTurbulence
                              type="fractalNoise"
                              baseFrequency="0.72"
                              numOctaves="4"
                              stitchTiles="stitch"
                        />
                        <feColorMatrix type="saturate" values="0" />
                  </filter>
                  <rect
                        width="100%"
                        height="100%"
                        filter="url(#suhab-grain-filter)"
                  />
            </StyledGrainSvg>
      );
}

export default function App() {
      useEffect(() => {
            let cancelled = false;
            const loadEmbeds = () => {
                  if (cancelled || typeof window.Tally === "undefined") {
                        return false;
                  }
                  window.Tally.loadEmbeds();
                  return true;
            };

            if (loadEmbeds()) {
                  return () => {
                        cancelled = true;
                  };
            }

            const interval = setInterval(() => {
                  if (loadEmbeds()) {
                        clearInterval(interval);
                  }
            }, 50);

            const timeout = setTimeout(() => clearInterval(interval), 15000);

            return () => {
                  cancelled = true;
                  clearInterval(interval);
                  clearTimeout(timeout);
            };
      }, []);

      const publicUrl = process.env.PUBLIC_URL || "";

      return (
            <AppRoot>
                  <Grain />
                  <Main>
                        <BrandMark aria-hidden="true">
                              <LanternIcon
                                    src={`${publicUrl}/images/lantern1.png`}
                                    alt=""
                              />
                        </BrandMark>

                        <Card>
                              <Title>
                                    <TitleLine1>Ne manque pas</TitleLine1>
                                    <TitleLine2>
                                          <span>le lancement de </span>
                                          <TitleWordmark
                                                src={`${publicUrl}/images/logosuhab.png`}
                                                alt="Suhab"
                                          />
                                          <span> !</span>
                                    </TitleLine2>
                              </Title>
                              <Subtitle>
                                    Fais du Coran un compagnon de vie
                              </Subtitle>
                              <TallyFrameWrap>
                                    <iframe
                                          title="Ne manque pas le lancement de Suhab"
                                          data-tally-src={TALLY_EMBED_URL}
                                          loading="lazy"
                                          width="100%"
                                          height="211"
                                          frameBorder={0}
                                    />
                              </TallyFrameWrap>
                              <Disclaimer>
                                    Ton e-mail ne sera utilisé que pour
                                    t&apos;informer du lancement.
                              </Disclaimer>
                        </Card>
                  </Main>
            </AppRoot>
      );
}

const AppRoot = styled.div`
      position: relative;
      min-height: 100vh;
      background-color: ${C.beigeMid};
      color: ${C.text};
`;

const StyledGrainSvg = styled.svg`
      position: fixed;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      opacity: 0.55;
      mix-blend-mode: overlay;
`;

const Main = styled.div`
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 48px 24px 56px;
      max-width: 520px;
      margin: 0 auto;
`;

const BrandMark = styled.div`
      display: inline-flex;
      margin-bottom: 28px;
`;

const LanternIcon = styled.img`
      height: 44px;
      width: auto;
      display: block;
      object-fit: contain;
`;

const Card = styled.div`
      width: 100%;
      background-color: ${C.beige};
      border-radius: 22px;
      border: 2px solid rgba(59, 35, 10, 0.14);
      box-shadow: 4px 5px 0 rgba(59, 35, 10, 0.06);
      padding: clamp(1.5rem, 4vw, 2.25rem);
      text-align: center;
`;

const Title = styled.h1`
      font-family: "Epilogue", system-ui, sans-serif;
      line-height: 1.2;
      letter-spacing: -0.025em;
      color: ${C.text};
      margin: 0 0 0.75rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 0.12em;
`;

const TitleLine1 = styled.span`
      font-size: clamp(1.1rem, 3.6vw, 1.4rem);
      font-weight: 500;
      display: block;
      width: 100%;
`;

const TitleLine2 = styled.span`
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 0.2em 0.3em;
      font-size: clamp(1.1rem, 3.6vw, 1.4rem);
      font-weight: 500;
      width: 100%;
`;

const TitleWordmark = styled.img`
      height: 1.6em;
      width: auto;
      max-width: min(10rem, 48vw);
      object-fit: contain;
      object-position: left center;
      flex-shrink: 0;
      margin: 0.1em 0;
      padding-bottom: 0.2em;
`;

const Subtitle = styled.p`
      font-family: "Playfair Display", Georgia, "Times New Roman", serif;
      font-style: italic;
      font-weight: 400;
      font-size: clamp(1.05rem, 2.8vw, 1.2rem);
      line-height: 1.55;
      color: ${C.text};
      opacity: 0.72;
      margin: 0 0 1.4rem;
      max-width: 22rem;
      margin-left: auto;
      margin-right: auto;
`;

const TallyFrameWrap = styled.div`
      width: 100%;
      min-height: 211px;
      text-align: left;

      iframe {
            display: block;
            border: 0;
            margin: 0;
            border-radius: 12px;
      }
`;

const Disclaimer = styled.p`
      margin-top: 1.25rem;
      font-family: "Roboto Mono", ui-monospace, monospace;
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: ${C.text};
      opacity: 0.38;
      line-height: 1.5;
`;
